package top.wcpe.allincore.ui.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.Node
import top.wcpe.allincore.ui.runtime.render.layer.DrawTraceEntry
import kotlin.js.Promise
import kotlin.js.json

internal enum class WebRenderSurfaceState {
    NEW,
    INITIALIZING,
    READY,
    FAILED,
    DISPOSED,
}

internal interface WebRenderHost {
    fun appendCanvas(canvas: dynamic)
    fun removeCanvas(canvas: dynamic)
}

internal interface WebRenderApplication {
    val canvas: dynamic
    val stage: Container
    fun init(options: dynamic): Promise<Unit>
    fun resize(width: Int, height: Int, resolution: Double)
    fun render()
    fun destroy()
}

internal class DomWebRenderHost(private val element: Element) : WebRenderHost {
    override fun appendCanvas(canvas: dynamic) {
        val node = requireNode(canvas)
        require(node.ownerDocument === element.ownerDocument) { "渲染画布必须与宿主元素属于同一 document" }
        require(!wouldCreateCycle(node)) { "渲染画布不能是宿主元素自身或其祖先，以免形成 DOM 循环" }
        element.appendChild(node)
    }

    override fun removeCanvas(canvas: dynamic) {
        val node = nodeOrNull(canvas) ?: return
        if (node.parentNode === element) {
            element.removeChild(node)
        }
    }

    private fun requireNode(value: dynamic): Node =
        nodeOrNull(value) ?: throw IllegalArgumentException("渲染画布必须是真实 DOM 节点")

    private fun nodeOrNull(value: dynamic): Node? {
        val ownerDocument = element.ownerDocument ?: return null
        val ownerWindow = ownerDocument.defaultView ?: return null
        val nodePrototype = ownerWindow.asDynamic().Node?.prototype ?: return null
        return if (nodePrototype.isPrototypeOf(value) as Boolean) value.unsafeCast<Node>() else null
    }

    private fun wouldCreateCycle(node: Node): Boolean {
        var current: Node? = element
        while (current != null) {
            if (current === node) return true
            current = current.parentNode
        }
        return false
    }
}

internal class PixiWebRenderApplication(
    private val delegate: Application = Application(),
) : WebRenderApplication {

    override val canvas: dynamic get() = delegate.canvas
    override val stage: Container get() = delegate.stage

    override fun init(options: dynamic): Promise<Unit> = delegate.init(options)

    override fun resize(width: Int, height: Int, resolution: Double) {
        delegate.renderer.resize(width, height, resolution)
    }

    override fun render() {
        delegate.renderer.render(delegate.stage)
    }

    override fun destroy() {
        delegate.destroy(true, json("children" to true, "texture" to false, "textureSource" to false))
    }
}

private fun webGlAvailable(): Boolean {
    if (!(js("typeof document !== 'undefined'") as Boolean)) return true
    val canvas = document.createElement("canvas").asDynamic()
    return canvas.getContext("webgl2") != null || canvas.getContext("webgl") != null
}

internal class WebRenderSurface(
    private val sessionId: String,
    private val host: WebRenderHost,
    private val applicationFactory: () -> WebRenderApplication = { PixiWebRenderApplication() },
    private val devicePixelRatioProvider: () -> Double = { window.devicePixelRatio },
    private val diagnostics: WebRenderDiagnosticSink = defaultWebRenderDiagnosticSink(),
    private val webGlAvailableProvider: () -> Boolean = ::webGlAvailable,
    private val textureResolver: (String) -> Texture? = { resource ->
        Assets.get(resource).unsafeCast<Texture?>()
    },
    private val traceConsumer: (DrawTraceEntry) -> Unit = {},
    initialWidth: Int = DEFAULT_WIDTH,
    initialHeight: Int = DEFAULT_HEIGHT,
    modelCompositionHost: ModelCompositionHost? = null,
) {

    var state: WebRenderSurfaceState = WebRenderSurfaceState.NEW
        private set

    var generation: Int = 0
        private set

    var devicePixelRatio: Double = 1.0
        private set

    private var width: Int = initialWidth
    private var height: Int = initialHeight
    private val resources = WebRenderResourceRegistry()
    private var application: WebRenderApplication? = null
    private var bridge: PixiRenderBridge? = null
    private var initPromise: Promise<Unit>? = null
    private var initTaskLease: WebRenderResourceLease? = null
    private var rejectInit: ((Throwable) -> Unit)? = null
    private var canvasRemoved = false
    private var applicationDestroyed = false
    private var inputAdapter: WebInputLifecycle? = null
    private var modelCompositionHostDisposed = false

    val canvas: dynamic get() = application?.canvas
    val viewportWidth: Int get() = width
    val viewportHeight: Int get() = height
    internal val stage: Container? get() = application?.stage

    val modelCompositionHost: ModelCompositionHost = modelCompositionHost ?: PlaceholderModelCompositionHost(
        sessionId,
        { generation },
        { state == WebRenderSurfaceState.DISPOSED },
        diagnostics,
    )

    fun init(): Promise<Unit> {
        if (state == WebRenderSurfaceState.DISPOSED) {
            emitStateDiagnostic(INIT_CAPABILITY_KEY, DISPOSED_CODE, "渲染画布已销毁，不能再次初始化")
            return rejectedPromise(SurfaceException(DISPOSED_CODE))
        }
        initPromise?.let { return it }

        state = WebRenderSurfaceState.INITIALIZING
        val capturedGeneration = ++generation
        initTaskLease = resources.register(WebRenderResourceKind.ASYNC_TASK)
        devicePixelRatio = normalizedDpr(devicePixelRatioProvider())
        if (!webGlAvailableProvider()) {
            releaseInitTask()
            state = WebRenderSurfaceState.FAILED
            emitWebGlUnavailable()
            emitInitializationFailure()
            return rejectedInitPromise(SurfaceException(WEBGL_UNAVAILABLE_CODE))
        }
        val created = runCatching(applicationFactory).getOrElse { error ->
            releaseInitTask()
            state = WebRenderSurfaceState.FAILED
            emitInitializationFailure()
            return rejectedInitPromise(error)
        }
        application = created
        canvasRemoved = false
        applicationDestroyed = false
        val initialization = runCatching { created.init(initOptions()) }.getOrElse { error ->
            releaseInitTask()
            cleanupApplication(created)
            state = WebRenderSurfaceState.FAILED
            emitInitializationFailure()
            return rejectedInitPromise(error)
        }
        val shared = createSharedInitPromise(created, initialization, capturedGeneration)
        initPromise = shared
        return shared
    }

    private fun createSharedInitPromise(
        created: WebRenderApplication,
        initialization: Promise<Unit>,
        capturedGeneration: Int,
    ): Promise<Unit> = Promise { resolve, reject ->
        rejectInit = { reject(it) }
        initialization.then(
            onFulfilled = { completeInitialization(created, capturedGeneration, resolve, reject) },
            onRejected = { reason -> failInitialization(created, capturedGeneration, reason, reject) },
        )
    }

    private fun completeInitialization(
        created: WebRenderApplication,
        capturedGeneration: Int,
        resolve: (Unit) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        releaseInitTask()
        if (!accepts(capturedGeneration)) {
            cleanupApplication(created)
            reject(SurfaceException(DISPOSED_DURING_INIT_CODE))
            return
        }
        try {
            host.appendCanvas(created.canvas)
            bridge = createBridge(created)
            state = WebRenderSurfaceState.READY
            rejectInit = null
            resolve(Unit)
        } catch (error: Throwable) {
            cleanupApplication(created)
            state = WebRenderSurfaceState.FAILED
            rejectInit = null
            emitInitializationFailure()
            reject(error)
        }
    }

    private fun failInitialization(
        created: WebRenderApplication,
        capturedGeneration: Int,
        reason: dynamic,
        reject: (Throwable) -> Unit,
    ) {
        releaseInitTask()
        if (state == WebRenderSurfaceState.DISPOSED || generation != capturedGeneration) {
            reject(SurfaceException(DISPOSED_DURING_INIT_CODE))
        } else {
            cleanupApplication(created)
            state = WebRenderSurfaceState.FAILED
            emitInitializationFailure()
            reject(asThrowable(reason))
        }
        rejectInit = null
    }

    private fun createBridge(created: WebRenderApplication): PixiRenderBridge {
        val capturedGeneration = generation
        lateinit var createdBridge: PixiRenderBridge
        createdBridge = PixiRenderBridge(
            created.stage,
            width.toFloat(),
            height.toFloat(),
            sessionId,
            { generation },
            diagnostics,
            textureResolver,
            resources,
            traceConsumer,
            modelCompositionHost,
        ) {
            presentAttachedTexture(created, createdBridge, capturedGeneration)
        }
        return createdBridge
    }

    private fun presentAttachedTexture(
        targetApplication: WebRenderApplication,
        sourceBridge: PixiRenderBridge,
        capturedGeneration: Int,
    ) {
        if (state != WebRenderSurfaceState.READY || generation != capturedGeneration) return
        if (application !== targetApplication || bridge !== sourceBridge) return
        if (runCatching(targetApplication::render).isFailure) emitFrameFailure()
    }

    fun resize(width: Int, height: Int, dpr: Double = devicePixelRatioProvider()): Boolean {
        if (!requireReady(RESIZE_CAPABILITY_KEY)) {
            return false
        }
        this.width = width.coerceAtLeast(1)
        this.height = height.coerceAtLeast(1)
        devicePixelRatio = normalizedDpr(dpr)
        application?.resize(this.width, this.height, devicePixelRatio)
        bridge?.resize(this.width.toFloat(), this.height.toFloat())
        return true
    }

    fun setLogicalViewport(width: Float, height: Float, scale: Float): Boolean {
        if (!requireReady(LOGICAL_VIEWPORT_CAPABILITY_KEY)) {
            return false
        }
        bridge?.resize(width.coerceAtLeast(1f), height.coerceAtLeast(1f))
        application?.stage?.scale?.set(scale.coerceAtLeast(0.01f))
        return true
    }

    fun renderFrame(draw: (PixiRenderBridge) -> Unit): Boolean {
        if (!requireReady(RENDER_CAPABILITY_KEY)) {
            return false
        }
        val frameBridge = bridge ?: return false
        return try {
            frameBridge.beginFrame()
            draw(frameBridge)
            application?.render()
            true
        } catch (_: Throwable) {
            emitFrameFailure()
            false
        } finally {
            frameBridge.endFrame()
        }
    }

    /** 安装或替换当前 surface 唯一输入适配器；surface 不持有业务焦点状态。 */
    fun installInputAdapter(adapter: WebInputLifecycle) {
        if (inputAdapter === adapter) {
            adapter.install()
            return
        }
        inputAdapter?.uninstall()
        inputAdapter = adapter
        adapter.install()
    }

    /** 卸载当前输入适配器；重复调用保持幂等。 */
    fun uninstallInputAdapter() {
        inputAdapter?.uninstall()
        inputAdapter = null
    }

    fun resourceCounts(): WebRenderResourceCounts = resources.snapshot()

    fun acquireTexture(resource: String): Promise<WebTextureLease?> {
        if (!requireReady(RENDER_CAPABILITY_KEY) || resource.isBlank()) return Promise.resolve(null)
        val capturedGeneration = generation
        val task = resources.register(WebRenderResourceKind.ASYNC_TASK)
        return Assets.load(resource).then { texture ->
            task.release()
            if (state != WebRenderSurfaceState.READY || generation != capturedGeneration) {
                Assets.unload(resource).catch { Unit }
                null
            } else {
                val leases = listOf(
                    resources.register(WebRenderResourceKind.BORROWED_TEXTURE) {
                        Assets.unload(resource).catch { Unit }
                    },
                    resources.register(WebRenderResourceKind.BORROWED_BASE_TEXTURE),
                )
                WebTextureLease(canonicalWebResourceKey(resource), texture, leases)
            }
        }.catch {
            task.release()
            null
        }
    }

    fun scheduleAnimationFrame(callback: () -> Unit): Boolean {
        if (!requireReady(RENDER_CAPABILITY_KEY)) return false
        val capturedGeneration = generation
        var frameId = -1
        lateinit var lease: WebRenderResourceLease
        lease = resources.register(WebRenderResourceKind.ANIMATION_FRAME) {
            if (frameId >= 0) window.cancelAnimationFrame(frameId)
        }
        frameId = window.requestAnimationFrame {
            frameId = -1
            lease.release()
            if (state == WebRenderSurfaceState.READY && generation == capturedGeneration) callback()
        }
        return true
    }

    fun dispose() {
        val firstAttempt = state != WebRenderSurfaceState.DISPOSED
        val wasInitializing = state == WebRenderSurfaceState.INITIALIZING
        if (firstAttempt) {
            generation++
            state = WebRenderSurfaceState.DISPOSED
        }
        if (wasInitializing) {
            rejectInit?.invoke(SurfaceException(DISPOSED_DURING_INIT_CODE))
            rejectInit = null
        }
        releaseInitTask()
        uninstallInputAdapter()
        disposeModelCompositionHost()
        disposeBridge()
        application?.let { target ->
            if (cleanupApplication(target)) application = null
        }
        if (!resources.disposeAll()) emitDisposalFailure(RESOURCE_DISPOSE_CAPABILITY_KEY)
    }

    private fun disposeModelCompositionHost() {
        if (modelCompositionHostDisposed) return
        if (runCatching(modelCompositionHost::dispose).isSuccess) {
            modelCompositionHostDisposed = true
        } else {
            emitDisposalFailure(WebRenderDiagnosticCapabilityKeys.MODEL_COMPOSITION)
        }
    }

    private fun disposeBridge() {
        val target = bridge ?: return
        val disposed = runCatching { target.dispose() }.getOrDefault(false)
        if (disposed) {
            bridge = null
        } else {
            emitDisposalFailure(RESOURCE_DISPOSE_CAPABILITY_KEY)
        }
    }

    private fun cleanupApplication(target: WebRenderApplication): Boolean {
        if (!canvasRemoved) {
            canvasRemoved = runCatching { host.removeCanvas(target.canvas) }.isSuccess
        }
        if (!applicationDestroyed) {
            applicationDestroyed = runCatching { target.destroy() }.isSuccess
        }
        val disposed = canvasRemoved && applicationDestroyed
        if (!disposed) emitDisposalFailure("")
        return disposed
    }

    private fun requireReady(capabilityKey: String): Boolean {
        if (state == WebRenderSurfaceState.READY) {
            return true
        }
        emitStateDiagnostic(
            capabilityKey,
            INVALID_STATE_CODE,
            "Web 渲染操作被拒绝，当前状态为 ${state.name}",
        )
        return false
    }

    private fun emitStateDiagnostic(capabilityKey: String, code: String, message: String) {
        diagnostics.emit(
            WebRenderDiagnostic(
                code,
                WebRenderDiagnosticSeverity.WARNING,
                message,
                sessionId,
                generation,
                capabilityKey,
                "",
                null,
                -1,
            ),
        )
    }

    private fun emitFrameFailure() {
        diagnostics.emit(
            WebRenderDiagnostic(
                FRAME_FAILED_CODE,
                WebRenderDiagnosticSeverity.ERROR,
                "Web 渲染帧执行失败，当前帧已停止提交",
                sessionId,
                generation,
                RENDER_CAPABILITY_KEY,
                "",
                null,
                -1,
            ),
        )
    }

    private fun emitWebGlUnavailable() {
        diagnostics.emit(
            WebRenderDiagnostic(
                WEBGL_UNAVAILABLE_CODE,
                WebRenderDiagnosticSeverity.ERROR,
                "浏览器未提供 WebGL 上下文，已停止 Pixi 初始化并进入 FAILED 状态",
                sessionId,
                generation,
                WEBGL_CAPABILITY_KEY,
                "",
                null,
                -1,
            ),
        )
    }

    private fun emitInitializationFailure() {
        diagnostics.emit(
            WebRenderDiagnostic(
                INIT_FAILED_CODE,
                WebRenderDiagnosticSeverity.ERROR,
                "Web 渲染画布初始化失败",
                sessionId,
                generation,
                INIT_CAPABILITY_KEY,
                "",
                null,
                -1,
            ),
        )
    }

    private fun emitDisposalFailure(capabilityKey: String) {
        diagnostics.emit(
            WebRenderDiagnostic(
                DISPOSE_FAILED_CODE,
                WebRenderDiagnosticSeverity.ERROR,
                "Web 渲染资源销毁失败，已继续清理其余资源",
                sessionId,
                generation,
                capabilityKey,
                "",
                null,
                -1,
            ),
        )
    }

    private fun releaseInitTask() {
        initTaskLease?.release()
        initTaskLease = null
    }

    private fun accepts(capturedGeneration: Int): Boolean =
        state == WebRenderSurfaceState.INITIALIZING && generation == capturedGeneration

    private fun initOptions(): dynamic = json(
        "width" to width,
        "height" to height,
        "resolution" to devicePixelRatio,
        "autoDensity" to true,
        "background" to "#111318",
        "autoStart" to false,
        "preference" to "webgl",
    )

    private fun normalizedDpr(value: Double): Double = if (value.isFinite() && value > 0.0) value else 1.0

    private fun rejectedInitPromise(error: Throwable): Promise<Unit> {
        val rejected = rejectedPromise(error)
        initPromise = rejected
        return rejected
    }

    private fun rejectedPromise(error: Throwable): Promise<Unit> = Promise { _, reject -> reject(error) }

    private fun asThrowable(reason: dynamic): Throwable = reason as? Throwable ?: SurfaceException(reason.toString())

    private class SurfaceException(message: String) : RuntimeException(message)

    private companion object {
        const val DEFAULT_WIDTH = 400
        const val DEFAULT_HEIGHT = 400
        const val DISPOSED_CODE = "WEB_RENDER_SURFACE_DISPOSED"
        const val DISPOSED_DURING_INIT_CODE = "WEB_RENDER_SURFACE_DISPOSED_DURING_INIT"
        const val INVALID_STATE_CODE = "WEB_RENDER_INVALID_STATE"
        const val INIT_FAILED_CODE = "WEB_RENDER_INIT_FAILED"
        const val FRAME_FAILED_CODE = "WEB_RENDER_FRAME_FAILED"
        const val DISPOSE_FAILED_CODE = "WEB_RENDER_DISPOSE_FAILED"
        const val WEBGL_UNAVAILABLE_CODE = "WEB_RENDER_WEBGL_UNAVAILABLE"
        const val INIT_CAPABILITY_KEY = WebRenderDiagnosticCapabilityKeys.SURFACE_INIT
        const val RESIZE_CAPABILITY_KEY = WebRenderDiagnosticCapabilityKeys.SURFACE_RESIZE
        const val LOGICAL_VIEWPORT_CAPABILITY_KEY = WebRenderDiagnosticCapabilityKeys.SURFACE_LOGICAL_VIEWPORT
        const val RENDER_CAPABILITY_KEY = WebRenderDiagnosticCapabilityKeys.SURFACE_RENDER
        const val RESOURCE_DISPOSE_CAPABILITY_KEY = WebRenderDiagnosticCapabilityKeys.RESOURCE_DISPOSE
        const val WEBGL_CAPABILITY_KEY = WebRenderDiagnosticCapabilityKeys.SURFACE_WEBGL
    }
}
